// Facility Appointments — history + filters (Phase 2 port, task B4).
//
// Port of legacy src/app/[locale]/[country]/account/facility/[facilityId]/
// appointments/{page.tsx,AppointmentsClient.tsx}: 4 stat cards, 5 status
// filter chips, free-text search + date + provider dropdown filters, and a
// history table (100 most recent appointments across all dates/statuses)
// with expandable rows on mobile. Data comes from the single
// GET /api/account/facility/:facilityId/appointments endpoint
// (apps/api/src/routes/account-facility-ops.ts) — stats are computed
// server-side in the same response; filtering happens client-side over the
// 100 rows, exactly like legacy (AppointmentsClient.tsx:149-176).
//
// Deviations from legacy (documented):
// - Service column shows from `md:` (legacy `lg:`) so every hidden column is
//   reachable via the mobile expand accordion (CLAUDE.md tables rule; legacy
//   left Service unreachable between md and lg).
// - `status` + `date` filters live in the URL (search params) — preserves the
//   old SPA page's deep-link contract; text search + provider stay local
//   state like legacy.
// - Statuses beyond the 4 legacy chip states (scheduled / in_progress /
//   no_show) render translated labels instead of raw i18n-key fallbacks.

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Calendar,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Search,
  XCircle,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

// ─── Types ──────────────────────────────────────────────────────────────────

interface AppointmentsSearch {
  /** ISO date YYYY-MM-DD — the "on this day" filter (legacy dateFilter). */
  date?: string;
  /** Status chip filter. */
  status?: "all" | "confirmed" | "pending" | "completed" | "cancelled";
}

interface PersonRef {
  id: string;
  firstName: string;
  lastName: string;
}

interface AppointmentRow {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
  consultationName: string | null;
  provider: PersonRef;
  profile: PersonRef;
}

interface AppointmentsResponse {
  appointments: AppointmentRow[];
  providers: PersonRef[];
  stats: {
    today: number;
    pending: number;
    completedMonth: number;
    cancelledMonth: number;
  };
}

const STATUS_TABS = ["all", "confirmed", "pending", "completed", "cancelled"] as const;
type StatusTab = (typeof STATUS_TABS)[number];

// ─── Route ──────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/appointments")({
  validateSearch: (search: Record<string, unknown>): AppointmentsSearch => {
    const dateRaw = typeof search.date === "string" ? search.date : undefined;
    const statusRaw = typeof search.status === "string" ? search.status : undefined;
    return {
      date: dateRaw && /^\d{4}-\d{2}-\d{2}$/.test(dateRaw) ? dateRaw : undefined,
      status: STATUS_TABS.includes(statusRaw as StatusTab)
        ? (statusRaw as AppointmentsSearch["status"])
        : undefined,
    };
  },
  component: FacilityAppointmentsPage,
});

// ─── Page ───────────────────────────────────────────────────────────────────

function FacilityAppointmentsPage() {
  const { facilityId } = Route.useParams();
  const { date, status } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { i18n, t } = useLingui();
  const locale = i18n.locale;

  const activeTab: StatusTab = status ?? "all";
  const dateFilter = date ?? "";

  const [search, setSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");

  const query = useQuery({
    queryKey: ["api", `/api/account/facility/${facilityId}/appointments`],
    queryFn: () =>
      apiFetch.get<AppointmentsResponse>(`/api/account/facility/${facilityId}/appointments`),
    retry: false,
  });

  const setSearchParams = (next: Partial<AppointmentsSearch>) => {
    void navigate({
      to: "/account/facility/$facilityId/appointments",
      params: { facilityId },
      search: (prev: AppointmentsSearch) => ({ ...prev, ...next }),
    });
  };

  const appointments = query.data?.appointments;

  // Client-side filtering — port of legacy AppointmentsClient.tsx:149-167
  // (including the UTC-based date comparison).
  const filtered = useMemo(() => {
    if (!appointments) return [];
    return appointments.filter((a) => {
      if (activeTab !== "all" && a.status !== activeTab) return false;
      if (providerFilter !== "all" && a.provider.id !== providerFilter) return false;
      if (dateFilter) {
        const apptDate = new Date(a.startsAt).toISOString().slice(0, 10);
        if (apptDate !== dateFilter) return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        const patientName = `${a.profile.firstName} ${a.profile.lastName}`.toLowerCase();
        const providerName = `${a.provider.firstName} ${a.provider.lastName}`.toLowerCase();
        const service = (a.consultationName ?? "").toLowerCase();
        if (!patientName.includes(q) && !providerName.includes(q) && !service.includes(q))
          return false;
      }
      return true;
    });
  }, [appointments, activeTab, providerFilter, dateFilter, search]);

  // Chip counts from the full (unfiltered) list — legacy :170-176.
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: appointments?.length ?? 0 };
    for (const tab of STATUS_TABS.slice(1)) {
      c[tab] = appointments?.filter((a) => a.status === tab).length ?? 0;
    }
    return c;
  }, [appointments]);

  if (query.isLoading) return <AppointmentsSkeleton />;
  if (query.error instanceof ApiError && (query.error.status === 403 || query.error.status === 404))
    return <NoAccessCard message={t`No access to this facility's appointments.`} />;
  if (query.error || !query.data)
    return <NoAccessCard message={t`Couldn't load appointments. Please try again.`} />;

  const { stats, providers } = query.data;

  const statCards: {
    key: string;
    label: ReactNode;
    value: number;
    icon: typeof Calendar;
    iconClass: string;
    bgClass: string;
  }[] = [
    {
      key: "today",
      label: <Trans>Today</Trans>,
      value: stats.today,
      icon: Calendar,
      iconClass: "text-primary",
      bgClass: "bg-primary-soft",
    },
    {
      key: "pending",
      label: <Trans>Pending</Trans>,
      value: stats.pending,
      icon: Clock,
      iconClass: "text-warning",
      bgClass: "bg-warning-soft",
    },
    {
      key: "completedMonth",
      label: <Trans>Completed this month</Trans>,
      value: stats.completedMonth,
      icon: CheckCircle2,
      iconClass: "text-success",
      bgClass: "bg-success-soft",
    },
    {
      key: "cancelledMonth",
      label: <Trans>Cancelled this month</Trans>,
      value: stats.cancelledMonth,
      icon: XCircle,
      iconClass: "text-destructive",
      bgClass: "bg-destructive-soft",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
            <Trans>Appointments</Trans>
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            <Trans>All clinic appointments</Trans>
          </p>
        </div>
        {/* Legacy page.tsx:104-107 renders this button without a handler —
            booking-from-desk ships with the booking flow (V1.1). */}
        <Button className="w-fit">
          <CalendarDays className="size-4" aria-hidden />
          <Trans>New appointment</Trans>
        </Button>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        {statCards.map((stat) => (
          <div key={stat.key} className="border-border bg-card rounded-xl border p-4 sm:p-5">
            <div className="flex items-center gap-2">
              <div className={cn("rounded-lg p-1.5", stat.bgClass)}>
                <stat.icon className={cn("size-4", stat.iconClass)} aria-hidden />
              </div>
              <p className="text-muted-foreground text-xs font-medium">{stat.label}</p>
            </div>
            <p className="text-foreground mt-3 text-2xl font-bold tabular-nums">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Status filter chips — wired to real data (counts over all 100 rows) */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setSearchParams({ status: tab === "all" ? undefined : tab })}
            className={cn(
              "inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
              "focus-visible:ring-primary/40 focus:outline-none focus-visible:ring-2",
              activeTab === tab
                ? "bg-primary text-primary-foreground"
                : "border-border bg-card text-muted-foreground hover:text-foreground border",
            )}
          >
            <StatusTabLabel tab={tab} />
            <span className="text-xs opacity-70">{counts[tab]}</span>
          </button>
        ))}
      </div>

      {/* Search & filters */}
      <div className="border-border bg-card rounded-xl border p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search
              className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2"
              aria-hidden
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t`Search by patient, doctor...`}
              className="border-border bg-background focus:border-primary focus:ring-primary/20 h-11 w-full rounded-lg border pr-4 pl-10 text-sm transition-colors outline-none focus:ring-2"
            />
          </div>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setSearchParams({ date: e.target.value || undefined })}
            aria-label={t`Date`}
            className="border-border bg-background focus:border-primary focus:ring-primary/20 h-11 rounded-lg border px-3 text-sm transition-colors outline-none focus:ring-2"
          />
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            aria-label={t`Doctor`}
            className="border-border bg-background text-foreground focus:border-primary focus:ring-primary/20 h-11 rounded-lg border px-3 text-sm transition-colors outline-none focus:ring-2"
          >
            <option value="all">{t`All doctors`}</option>
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                Dr. {p.firstName} {p.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* History table */}
      <div className="border-border bg-card rounded-xl border">
        {filtered.length === 0 ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center rounded-xl p-12 text-center">
            <div className="bg-primary-soft rounded-xl p-4">
              <CalendarDays className="text-primary size-8" aria-hidden />
            </div>
            <h3 className="text-foreground mt-4 text-base font-semibold">
              <Trans>No appointments found</Trans>
            </h3>
            <p className="text-muted-foreground mt-2 max-w-sm text-sm">
              <Trans>
                Appointments will appear here as patients book consultations at your clinic.
              </Trans>
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-border bg-muted/50 border-b">
                  <HeadCell className="pr-3 pl-4">
                    <Trans>Date</Trans>
                  </HeadCell>
                  <HeadCell className="px-3">
                    <Trans>Time</Trans>
                  </HeadCell>
                  <HeadCell className="px-3">
                    <Trans>Patient</Trans>
                  </HeadCell>
                  <HeadCell className="hidden px-3 md:table-cell">
                    <Trans>Doctor</Trans>
                  </HeadCell>
                  <HeadCell className="hidden px-3 md:table-cell">
                    <Trans>Service</Trans>
                  </HeadCell>
                  <HeadCell className="px-3">
                    <Trans>Status</Trans>
                  </HeadCell>
                  <th className="py-3 pr-4 pl-3 md:hidden" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((appt) => (
                  <ExpandableRow key={appt.id} appt={appt} locale={locale} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AccountSecurityBanner />
    </div>
  );
}

// ─── Table pieces ───────────────────────────────────────────────────────────

function HeadCell({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <th
      className={cn(
        "text-muted-foreground py-3 text-left text-xs font-semibold tracking-wide uppercase",
        className,
      )}
    >
      {children}
    </th>
  );
}

function ExpandableRow({ appt, locale }: { appt: AppointmentRow; locale: string }) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const startsAt = new Date(appt.startsAt);
  const date = startsAt.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = startsAt.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return (
    <>
      <tr
        className="border-border hover:bg-muted/50 cursor-pointer border-b transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="text-foreground py-3 pr-3 pl-4 text-sm font-medium whitespace-nowrap">
          {date}
        </td>
        <td className="text-muted-foreground px-3 py-3 text-sm whitespace-nowrap tabular-nums">
          {time}
        </td>
        <td className="text-foreground px-3 py-3 text-sm font-medium">
          {appt.profile.firstName} {appt.profile.lastName}
        </td>
        <td className="text-muted-foreground hidden px-3 py-3 text-sm md:table-cell">
          Dr. {appt.provider.firstName} {appt.provider.lastName}
        </td>
        <td className="text-muted-foreground hidden px-3 py-3 text-sm md:table-cell">
          {appt.consultationName ?? "—"}
        </td>
        <td className="px-3 py-3">
          <StatusBadge status={appt.status} />
        </td>
        {/* Mobile expand toggle */}
        <td className="py-3 pr-4 pl-3 text-right md:hidden">
          <button
            type="button"
            aria-label={expanded ? t`Collapse row` : t`Expand row`}
            aria-expanded={expanded}
            className="text-muted-foreground hover:text-foreground inline-flex size-11 items-center justify-center rounded-lg"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? (
              <ChevronUp className="size-4" aria-hidden />
            ) : (
              <ChevronDown className="size-4" aria-hidden />
            )}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-border bg-muted/30 border-b md:hidden">
          <td colSpan={7} className="px-4 py-3">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">
                <Trans>Doctor</Trans>
              </dt>
              <dd className="text-foreground">
                Dr. {appt.provider.firstName} {appt.provider.lastName}
              </dd>
              <dt className="text-muted-foreground">
                <Trans>Service</Trans>
              </dt>
              <dd className="text-foreground">{appt.consultationName ?? "—"}</dd>
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Status rendering ───────────────────────────────────────────────────────

function StatusTabLabel({ tab }: { tab: StatusTab }) {
  switch (tab) {
    case "all":
      return <Trans>All</Trans>;
    case "confirmed":
      return <Trans>Confirmed</Trans>;
    case "pending":
      return <Trans>Pending</Trans>;
    case "completed":
      return <Trans>Completed</Trans>;
    case "cancelled":
      return <Trans>Cancelled</Trans>;
  }
}

// Legacy AppointmentsClient.tsx:39-52 color map → Badge variants:
// confirmed=teal→secondary, completed=blue→info, cancelled=red→destructive,
// pending=amber→warning, anything else→neutral.
function StatusBadge({ status }: { status: string }) {
  const variant = (
    {
      confirmed: "secondary",
      completed: "info",
      cancelled: "destructive",
      pending: "warning",
    } as const
  )[status];
  return <Badge variant={variant ?? "neutral"}>{statusLabel(status)}</Badge>;
}

function statusLabel(status: string): ReactNode {
  switch (status) {
    case "confirmed":
      return <Trans>Confirmed</Trans>;
    case "pending":
      return <Trans>Pending</Trans>;
    case "completed":
      return <Trans>Completed</Trans>;
    case "cancelled":
      return <Trans>Cancelled</Trans>;
    case "scheduled":
      return <Trans>Scheduled</Trans>;
    case "in_progress":
      return <Trans>In progress</Trans>;
    case "no_show":
      return <Trans>No-show</Trans>;
    default:
      return status;
  }
}

// ─── Skeleton / error states ────────────────────────────────────────────────

function AppointmentsSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-11 w-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-16 rounded-xl" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}

function NoAccessCard({ message }: { message: string }) {
  return (
    <div className="border-destructive/20 bg-card flex flex-col items-center gap-3 rounded-2xl border p-8 text-center">
      <AlertTriangle className="text-destructive size-8" aria-hidden />
      <p className="text-muted-foreground text-sm">{message}</p>
      <Link to="/account/user/dashboard" className="mt-2">
        <Button variant="outline">
          <Trans>Back to dashboard</Trans>
        </Button>
      </Link>
    </div>
  );
}
